import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AvikaLogo } from '../components/AvikaLogo';

// ── Animated number counter (hidden until real numbers are available) ─────────
const Counter: React.FC<{ to: number; suffix?: string; duration?: number }> = ({
  to, suffix = '', duration = 1800,
}) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const steps = 60;
    const inc = to / steps;
    let cur = 0;
    const t = setInterval(() => {
      cur = Math.min(cur + inc, to);
      setVal(Math.round(cur));
      if (cur >= to) clearInterval(t);
    }, duration / steps);
    return () => clearInterval(t);
  }, [to, duration]);
  return <>{val.toLocaleString()}{suffix}</>;
};

// ── Feature card ─────────────────────────────────────────────────────────────
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  desc: string;
  delay?: string;
}
const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, desc, delay = '0ms' }) => (
  <div
    className="group relative rounded-2xl p-7 transition-all duration-300 cursor-default
               hover:-translate-y-1 hover:shadow-2xl animate-fade-up"
    style={{
      background: '#FFFFFF',
      border: '1px solid rgba(196,149,106,0.28)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      animationDelay: delay,
    }}
  >
    {/* Hover glow */}
    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
      style={{ background: 'radial-gradient(circle at 50% 0%, rgba(196,149,106,0.08) 0%, transparent 70%)' }} />

    <div className="relative">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
        style={{ background: 'rgba(196,149,106,0.12)', border: '1px solid rgba(196,149,106,0.25)' }}>
        {icon}
      </div>
      <h3 className="font-display text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>
        {title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: '#555555' }}>
        {desc}
      </p>
    </div>
  </div>
);

// ── Step badge ────────────────────────────────────────────────────────────────
const Step: React.FC<{ n: number; title: string; desc: string }> = ({ n, title, desc }) => (
  <div className="flex gap-5 animate-fade-up">
    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
      style={{ background: 'linear-gradient(135deg, #C4956A, #E8C4A0)', color: '#141414' }}>
      {n}
    </div>
    <div>
      <h4 className="font-semibold text-base mb-1" style={{ color: '#1A1A1A' }}>{title}</h4>
      <p className="text-sm leading-relaxed" style={{ color: '#555555' }}>{desc}</p>
    </div>
  </div>
);

// ── Geometric background decoration ──────────────────────────────────────────
const BgDecor = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {/* Large faint circle top-right */}
    <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-5"
      style={{ background: 'radial-gradient(circle, #C4956A 0%, transparent 70%)' }} />
    {/* Bottom-left arc */}
    <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-5"
      style={{ background: 'radial-gradient(circle, #C4956A 0%, transparent 70%)' }} />
    {/* Thin horizontal gold rule across hero */}
    <div className="absolute top-[42%] left-0 right-0 h-px opacity-10"
      style={{ background: 'linear-gradient(90deg, transparent, #C4956A, transparent)' }} />
  </div>
);

// ── Nav link ──────────────────────────────────────────────────────────────────
const NavLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href}
    className="text-sm font-medium transition-colors duration-200 hover:text-avika-gold"
    style={{ color: '#777777' }}>
    {children}
  </a>
);

// ── Main component ────────────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div style={{ background: '#F8F6F2', color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>

      {/* ── Navbar ────────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'py-3 backdrop-blur-lg' : 'py-5'
      }`}
        style={{
          borderBottom: scrolled ? '1px solid rgba(196,149,106,0.20)' : 'none',
          background: scrolled ? 'rgba(248,246,242,0.95)' : 'transparent',
        }}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <AvikaLogo height={44} variant="dark" />

          <div className="hidden md:flex items-center gap-8">
            <NavLink href="#features">Features</NavLink>
            <NavLink href="#how-it-works">How it works</NavLink>
            <NavLink href="#compliance">Compliance</NavLink>
          </div>

          <Link to="/login">
            <button className="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200
                               hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #C4956A, #E8C4A0)',
                color: '#141414',
                boxShadow: '0 0 20px rgba(196,149,106,0.30)',
              }}>
              Sign In
            </button>
          </Link>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center
                          text-center px-6 pt-24 pb-16">
        <BgDecor />

        {/* Hero pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23C4956A' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E\")" }}
        />

        <div className="relative z-10 max-w-4xl mx-auto">

          {/* Eyebrow pill */}
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-8 text-xs font-semibold
                          uppercase tracking-widest animate-fade-up"
            style={{
              border: '1px solid rgba(196,149,106,0.4)',
              background: 'rgba(196,149,106,0.08)',
              color: '#C4956A',
              animationDelay: '0ms',
            }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#C4956A' }} />
            POSH Act 2013 · Compliance Platform
          </div>

          {/* Animate logo mark in hero */}
          <div className="flex justify-center mb-10 animate-float">
            <AvikaLogo height={110} variant="dark" />
          </div>

          <h1 className="font-display font-light leading-tight mb-6 animate-fade-up"
            style={{ fontSize: 'clamp(2.6rem, 6vw, 5rem)', animationDelay: '100ms' }}>
            Workplace safety,{' '}
            <span className="text-gold-shimmer font-semibold italic">redefined.</span>
          </h1>

          <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10 animate-fade-up"
            style={{ color: '#9E9A95', animationDelay: '200ms' }}>
            Avika is India's most thoughtful POSH compliance platform — guiding organisations through
            assessment, evidence management, and maturity reporting with elegance and precision.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center animate-fade-up"
            style={{ animationDelay: '300ms' }}>
            <Link to="/login">
              <button className="group flex items-center gap-2 px-8 py-3.5 rounded-full
                                 font-semibold text-sm transition-all duration-200 hover:scale-105 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, #C4956A, #E8C4A0)',
                  color: '#141414',
                  boxShadow: '0 4px 24px rgba(196,149,106,0.35)',
                }}>
                Get Started
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </Link>
            <a href="#features"
              className="px-8 py-3.5 rounded-full font-semibold text-sm transition-all duration-200
                         hover:bg-white/5"
              style={{ border: '1px solid rgba(196,149,106,0.35)', color: '#C4956A' }}>
              Explore features
            </a>
          </div>

        </div>
      </section>

      {/* ── Stats bar — hidden until real numbers are available ────────
           To show: change `false` to `true` on the next line        */}
      {false && (
        <section style={{ borderTop: '1px solid rgba(196,149,106,0.15)', borderBottom: '1px solid rgba(196,149,106,0.15)', background: 'rgba(196,149,106,0.05)' }}>
          <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 500,   suffix: '+',  label: 'Organisations' },
              { value: 98,    suffix: '%',  label: 'Compliance rate' },
              { value: 12000, suffix: '+',  label: 'Assessments done' },
              { value: 24,    suffix: '/7', label: 'Platform uptime' },
            ].map((s) => (
              <div key={s.label}>
                <p className="font-display text-4xl font-semibold mb-1" style={{ color: '#C4956A' }}>
                  <Counter to={s.value} suffix={s.suffix} />
                </p>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#888888' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Features ──────────────────────────────────────────────────── */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-widest mb-3 font-semibold" style={{ color: '#C4956A' }}>
            Everything you need
          </p>
          <h2 className="font-display font-light" style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>
            Built for compliance.<br />
            <span className="italic" style={{ color: '#C4956A' }}>Designed for people.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <FeatureCard
            delay="0ms"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>}
            title="POSH Assessment"
            desc="Structured questionnaire covering all 14 POSH Act requirements. Role-based workflows ensure the right people see the right questions."
          />
          <FeatureCard
            delay="80ms"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>}
            title="Evidence Manager"
            desc="Securely upload, tag, and organise supporting documents. Every piece of evidence is linked to the relevant compliance requirement."
          />
          <FeatureCard
            delay="160ms"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
            title="Maturity Scoring"
            desc="Real-time maturity index across six dimensions — Policy, Process, Training, ICC, Investigation, and Culture."
          />
          <FeatureCard
            delay="240ms"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
            title="PDF Compliance Reports"
            desc="Generate audit-ready PDF reports in one click — formatted for regulatory submissions and board presentations."
          />
          <FeatureCard
            delay="320ms"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
            title="Role-based Access"
            desc="Super Admin, Org Admin, Assessor, ICC Member, and Viewer roles — each with precisely scoped permissions."
          />
          <FeatureCard
            delay="400ms"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
            title="Enterprise Security"
            desc="TOTP MFA, httpOnly cookie auth, refresh token rotation, audit trail, rate limiting, and email verification — OWASP compliant by design."
          />
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────── */}
      <section id="how-it-works"
        style={{ borderTop: '1px solid rgba(196,149,106,0.15)', background: 'rgba(196,149,106,0.04)' }}>
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-center">

            <div>
              <p className="text-xs uppercase tracking-widest mb-3 font-semibold" style={{ color: '#C4956A' }}>
                Simple. Structured. Secure.
              </p>
              <h2 className="font-display font-light mb-10" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}>
                Three steps to{' '}
                <span className="italic" style={{ color: '#C4956A' }}>full compliance</span>
              </h2>

              <div className="space-y-8">
                <Step n={1} title="Onboard your organisation"
                  desc="Set up your organisation profile, add departments, and invite your ICC members and assessors." />
                <Step n={2} title="Complete the POSH assessment"
                  desc="Work through the structured checklist, attach supporting evidence, and track your progress in real time." />
                <Step n={3} title="Generate your compliance report"
                  desc="Get a detailed maturity score with actionable recommendations and a board-ready PDF in one click." />
              </div>
            </div>

            {/* Decorative card stack */}
            <div className="relative hidden md:flex items-center justify-center h-80">
              {/* Card 3 (back) */}
              <div className="absolute w-56 h-64 rounded-2xl -rotate-6 opacity-20"
                style={{ background: 'rgba(196,149,106,0.2)', border: '1px solid rgba(196,149,106,0.3)', top: '10%', left: '10%' }} />
              {/* Card 2 */}
              <div className="absolute w-56 h-64 rounded-2xl rotate-3 opacity-40"
                style={{ background: 'rgba(196,149,106,0.1)', border: '1px solid rgba(196,149,106,0.3)', top: '5%', left: '18%' }} />
              {/* Card 1 (front) */}
              <div className="relative w-64 h-72 rounded-2xl p-6 animate-float"
                style={{
                  background: 'linear-gradient(145deg, rgba(196,149,106,0.12), rgba(196,149,106,0.04))',
                  border: '1px solid rgba(196,149,106,0.35)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                }}>
                <AvikaLogo height={52} variant="dark" className="mb-5" />
                <div className="space-y-2.5">
                  {['Policy & Governance', 'ICC Composition', 'Training Records', 'Complaint Process'].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 text-xs" style={{ color: '#555555' }}>
                      <span className="w-3.5 h-3.5 rounded-full flex-shrink-0 flex items-center justify-center"
                        style={{ background: 'rgba(196,149,106,0.25)', border: '1px solid #C4956A' }}>
                        <svg className="w-2 h-2" fill="#C4956A" viewBox="0 0 8 8">
                          <path d="M1 4l2 2 4-4" stroke="#C4956A" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        </svg>
                      </span>
                      {item}
                    </div>
                  ))}
                </div>
                <div className="absolute bottom-5 right-5">
                  <div className="text-xs font-semibold px-3 py-1 rounded-full"
                    style={{ background: 'rgba(196,149,106,0.15)', color: '#C4956A', border: '1px solid rgba(196,149,106,0.3)' }}>
                    92% compliant
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Compliance section ─────────────────────────────────────────── */}
      <section id="compliance" className="max-w-6xl mx-auto px-6 py-20">
        <div className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(196,149,106,0.10) 0%, rgba(196,149,106,0.04) 100%)',
            border: '1px solid rgba(196,149,106,0.20)',
          }}>
          <div className="absolute inset-0 opacity-5 pointer-events-none"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23C4956A' fill-opacity='1'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E\")" }} />
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-widest mb-4 font-semibold" style={{ color: '#C4956A' }}>
              Indian Labour Law · Ministry of Women & Child Development
            </p>
            <h2 className="font-display font-light mb-5" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.6rem)' }}>
              Fully aligned with the{' '}
              <span className="italic" style={{ color: '#C4956A' }}>Sexual Harassment of Women</span>
              <br />at Workplace (Prevention, Prohibition & Redressal) Act, 2013
            </h2>
            <p className="text-sm max-w-xl mx-auto mb-10" style={{ color: '#555555' }}>
              Every feature in Avika is mapped to a specific POSH Act requirement.
              No guesswork. No gaps. Just compliance you can trust.
            </p>
            <Link to="/login">
              <button className="px-10 py-4 rounded-full font-semibold text-base transition-all duration-200
                                 hover:scale-105 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, #C4956A, #E8C4A0)',
                  color: '#141414',
                  boxShadow: '0 4px 30px rgba(196,149,106,0.40)',
                }}>
                Start your compliance journey →
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid rgba(196,149,106,0.15)' }}>
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <AvikaLogo height={38} variant="dark" />
          <p className="text-xs text-center" style={{ color: '#777777' }}>
            © {new Date().getFullYear()} Avika. POSH Compliance Platform. All rights reserved.
          </p>
          <div className="flex gap-6 text-xs" style={{ color: '#6E6A65' }}>
            <a href="#features" className="hover:text-avika-gold transition-colors">Features</a>
            <Link to="/login" className="hover:text-avika-gold transition-colors" style={{ color: '#6E6A65' }}>Sign In</Link>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
